/* GrowthOS — WhatsApp chat modal for the lead record.
   Ported from design growthos/Modals.WhatsAppModal: a WhatsApp-style chat surface
   (header with the lead's name/phone, message thread, template insert, composer).
   The app has no WhatsApp Business backend, so the thread is client-side only —
   sending appends an outgoing bubble. Real lead name/phone are shown. */
import { useEffect, useRef, useState } from 'react';
import { Ic } from "../../shared/component/GosIcon";


const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const nowTime = () => new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const LeadWhatsAppModal = ({ lead = {}, onClose }) => {
  const fullName = lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';
  const first = fullName.split(' ')[0];
  const company = lead.companyName || 'your company';
  const phone = lead.mobile || lead.phone || '';

  const TEMPLATES = [
    { label: 'Introduction', text: `Hi ${first}, thanks for connecting! I'd love to share how we can help ${company}.` },
    { label: 'Follow-up', text: `Hi ${first}, just following up on our conversation — do you have a few minutes this week?` },
    { label: 'Share proposal', text: `Hi ${first}, I've put together a proposal for ${company}. Let me know your thoughts!` },
    { label: 'Schedule a call', text: `Hi ${first}, would you be open to a quick call? Happy to work around your schedule.` },
  ];

  const [thread, setThread] = useState([]);
  const [text, setText] = useState('');
  const [tmplOpen, setTmplOpen] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [thread]);

  const send = () => {
    if (!text.trim()) return;
    setThread((t) => [...t, { dir: 'out', text: text.trim(), time: nowTime() }]);
    setText('');
  };

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gos-wa-modal" role="dialog" aria-modal="true" aria-label="WhatsApp chat">
        <div className="gos-wa-head">
          <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={onClose} aria-label="Close">
            <Ic name="chevL" size={18} />
          </button>
          <span className="gos-wa-ava" style={{ background: avaColor(fullName) }}>{initials(fullName)}</span>
          <div className="gos-wa-head-meta">
            <div className="gos-wa-name">{fullName}</div>
            <div className="gos-wa-phone">{phone || 'No number on file'}</div>
          </div>
          <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" aria-label="Search"><Ic name="search" size={17} /></button>
          <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" aria-label="More"><Ic name="dots" size={17} /></button>
        </div>

        <div className="gos-wa-body" ref={bodyRef}>
          {thread.length === 0 ? (
            <div className="gos-wa-empty">No messages yet. Start the conversation below.</div>
          ) : (
            <>
              <div className="gos-wa-day">Today</div>
              {thread.map((m, i) => (
                <div key={i} className={'gos-wa-bubble ' + (m.dir === 'out' ? 'gos-wa-out' : 'gos-wa-in')}>
                  {m.text}
                  <span className="gos-wa-meta">{m.time}{m.dir === 'out' ? ' ✓✓' : ''}</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="gos-wa-foot">
          {tmplOpen ? (
            <>
              <div className="gos-wa-tmpl-backdrop" onClick={() => setTmplOpen(false)} />
              <div className="gos-wa-tmpl-menu">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    className="gos-wa-tmpl-item"
                    onClick={() => { setText(t.text); setTmplOpen(false); }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          <div className="gos-wa-input">
            <button type="button" className="gos-wa-tmpl" onClick={() => setTmplOpen((o) => !o)}>
              <Ic name="template" size={14} /> Template
            </button>
            <input
              placeholder="Type your message here"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
            />
            <button type="button" className="gos-wa-send" onClick={send} title="Send" disabled={!text.trim()}>
              <Ic name="send" size={17} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadWhatsAppModal;
